var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classnames from 'classnames';
import { Popover } from '../Popover';
import { OptionList } from '../OptionList';
import { BodyText } from '../BodyText';
import { Spinner } from '../Spinner';
import { Stack } from '../Stack';
import { AnvilSelectFooter, AnvilSelectHeader, SelectDrillInHeader, SelectDrillInFooter, SelectDrillInContent, Trigger, FocusProvider, } from './components';
import { FocusActionType, FocusContext, getInitialState, doesHeaderExist, doesFooterExist, getNextOption, focusFirstOption, getPreviousOption, focusLastOption, } from './utils';
import { keys } from '../../utilities/keyCodes';
// This is only used for Storybook to populate proptable
export const AnvilSelectSB = (props) => {
    return React.createElement(AnvilSelect, Object.assign({}, props));
};
export class AnvilSelectWrapped extends React.Component {
    constructor(props) {
        super(props);
        this.handleKeyDown = (e, option) => {
            var _a;
            const handledKeys = new Set([
                keys.esc,
                keys.down,
                keys.up,
                keys.enter,
                keys.space,
            ]);
            if (handledKeys.has(e.key)) {
                e.preventDefault();
            }
            if (e.key === keys.esc) {
                this.toggleOpenState();
                return;
            }
            if (e.key === keys.tab && !e.shiftKey) {
                const footer = doesFooterExist(e.target);
                if (!footer) {
                    e.preventDefault();
                    this.toggleOpenState();
                    return;
                }
            }
            if (e.key === keys.tab && e.shiftKey) {
                const header = doesHeaderExist(e.target);
                if (!header) {
                    e.preventDefault();
                    this.toggleOpenState();
                    return;
                }
            }
            if (e.key === keys.down) {
                const nextOption = getNextOption(e.currentTarget);
                if (!nextOption) {
                    focusFirstOption(e.currentTarget);
                }
                else {
                    nextOption.focus();
                }
                return;
            }
            if (e.key === keys.up) {
                const previousOption = getPreviousOption(e.currentTarget);
                if (!previousOption) {
                    focusLastOption(e.currentTarget);
                }
                else {
                    previousOption.focus();
                }
                return;
            }
            if (e.key === keys.enter || e.key === keys.space) {
                if (this.props.multiple) {
                    const shouldCheck = !this.state.checkboxState.some((checkbox) => checkbox.value === option.value);
                    const childObject = (_a = option === null || option === void 0 ? void 0 : option.options) !== null && _a !== void 0 ? _a : [];
                    this.onCheckboxChange(option, shouldCheck, childObject, option);
                    this.context.focusDispatch({
                        type: FocusActionType.SetFocus,
                        payload: { optionFocus: option },
                    });
                }
                else {
                    this.changeRadioState(option);
                    this.toggleOpenState();
                }
            }
        };
        this.focus = () => {
            const initialState = getInitialState(this.props.search, this.props.header, this.getOptionsForFocusContext(), false, !!this.props.tree);
            if (!this.state.openState) {
                initialState.triggerFocus = true;
            }
            this.context.focusDispatch({
                type: FocusActionType.SetFocus,
                payload: initialState,
            });
        };
        this.blur = () => {
            const initialState = getInitialState(this.props.search, this.props.header, this.getOptionsForFocusContext(), false, !!this.props.tree);
            initialState.triggerFocus = false;
            this.context.focusDispatch({
                type: FocusActionType.SetFocus,
                payload: initialState,
            });
        };
        /*
         * Internal state management of Select
         */
        this.toggleOpenState = (clickOutside = false) => {
            var _a, _b;
            const initialState = getInitialState(this.props.search, this.props.header, this.getOptionsForFocusContext(), clickOutside, !!this.props.tree);
            if (this.state.openState && !clickOutside) {
                initialState.triggerFocus = true;
            }
            this.context.focusDispatch({
                type: FocusActionType.SetFocus,
                payload: initialState,
            });
            const newOpenState = !this.state.openState;
            this.setState({
                openState: newOpenState,
            });
            (_b = (_a = this.props).onOpenChange) === null || _b === void 0 ? void 0 : _b.call(_a, newOpenState, this.state.checkboxState);
        };
        this.changeCheckboxState = (value, checked, childObject, selfObject) => {
            this.setState({
                checkboxState: value,
            });
            this.handleOnChange(value, checked, childObject, selfObject);
        };
        this.onCheckboxChange = (value, checked, childObject, selfObject) => {
            let newValue;
            const incomingValue = selfObject.value !== undefined ? selfObject.value : selfObject;
            if (checked) {
                newValue = [...this.state.checkboxState, selfObject];
            }
            else {
                newValue = this.state.checkboxState.filter((item) => {
                    return item.value !== incomingValue;
                });
            }
            this.changeCheckboxState(newValue, checked, childObject, selfObject);
            this.context.focusDispatch({
                type: FocusActionType.SetFocus,
                payload: { optionFocus: selfObject },
            });
        };
        this.changeRadioState = (value) => {
            this.setState({
                radioState: value,
            });
            this.handleOnChange(value, null, null, null);
        };
        this.getMultipleValues = () => {
            const defaults = {
                groupSelectAll: false,
                onTagClose: false,
                selectAll: false,
            };
            if (typeof this.props.multiple !== 'boolean')
                return Object.assign(Object.assign({}, defaults), this.props.multiple);
            return defaults;
        };
        this.getEmptyResult = () => {
            const message = this.props.emptyResult !== undefined
                ? this.props.emptyResult
                : 'No results found';
            return (React.createElement(BodyText, { className: "ta-center", subdued: true, size: "small" }, message));
        };
        this.checkDrillIn = () => {
            if (!this.props.drillIn || !this.props.drillIn.open)
                return false;
            return true;
        };
        this.getCheckboxStateFilteredByGroupValues = (flattenGroupValues) => {
            return this.state.checkboxState.filter((item) => !flattenGroupValues.find((groupValueItem) => item.value === groupValueItem.value));
        };
        this.checkIfDisabledItemIsSelected = (option) => {
            return (this.state.checkboxState.filter((item) => item.value === option.value).length > 0);
        };
        this.getFlattenValuesFromOptions = (options, skipDisabled) => {
            const result = [];
            if (options) {
                options.forEach((option) => {
                    if (option.options) {
                        result.push(...this.getFlattenValuesFromOptions(option.options));
                    }
                    else if (!option.disabled) {
                        result.push(option);
                    }
                    else if (!skipDisabled &&
                        this.checkIfDisabledItemIsSelected(option)) {
                        result.push(option);
                    }
                });
            }
            return result;
        };
        this.getChildOptionsFromGroupOptions = (groupOptions = []) => {
            return groupOptions.flatMap(({ options = [] }) => options);
        };
        /*
         * Handle functions
         */
        this.handleOnSelectAll = () => {
            if (this.props.multiple) {
                this.changeCheckboxState(this.getFlattenValuesFromOptions(this.props.options), true, this.getChildOptionsFromGroupOptions(this.props.options), [...this.props.options]);
            }
        };
        this.handleOnSelectGroup = (groupOptions) => {
            const groupValues = this.getFlattenValuesFromOptions(groupOptions);
            const checkboxStateFiltered = this.getCheckboxStateFilteredByGroupValues(groupValues);
            this.changeCheckboxState([...checkboxStateFiltered, ...groupValues], true, this.getChildOptionsFromGroupOptions(groupOptions), [...groupOptions]);
        };
        this.handleOnClear = () => {
            if (this.props.multiple) {
                const clearAllButDisabled = this.state.checkboxState.filter((item) => item.disabled);
                const self = this.state.checkboxState.filter((item) => !item.disabled);
                this.changeCheckboxState(clearAllButDisabled, false, [], self);
            }
            else {
                this.changeRadioState();
            }
        };
        this.handleOnClearGroup = (groupOptions) => {
            const groupValues = this.getFlattenValuesFromOptions(groupOptions, true);
            const newGroupValues = this.getCheckboxStateFilteredByGroupValues(groupValues);
            this.changeCheckboxState(newGroupValues, false, [], [...groupOptions]);
        };
        this.handleOnChange = (value, checked, childObject, selfObject) => {
            if (this.props.onChange) {
                this.props.onChange(value, checked, childObject, selfObject);
            }
        };
        this.handleOnTagClose = (value) => {
            this.onCheckboxChange(value, false, value, value);
        };
        this.handleClickOutside = () => {
            if (this.props.onClickOutside)
                this.props.onClickOutside();
            if (!this.state.openState || !this.props.closeOnClickOutside)
                return;
            this.toggleOpenState(true);
        };
        this.handleOnClick = (e) => {
            var _a, _b;
            (_b = (_a = this.props.trigger) === null || _a === void 0 ? void 0 : _a.onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e);
            this.toggleOpenState();
        };
        this.getOnChange = (value, checked, childObject, selfObject) => {
            if (this.props.multiple)
                return this.onCheckboxChange(value, checked, childObject, selfObject);
            this.toggleOpenState();
            return this.changeRadioState(selfObject);
        };
        this.getFlatGroup = () => {
            if (this.props.tree)
                return false;
            if (this.getMultipleValues().groupSelectAll) {
                return {
                    onGroupSelectAll: this.handleOnSelectGroup,
                    onGroupSelectNone: this.handleOnClearGroup,
                };
            }
            return true;
        };
        this.getOnExpand = () => {
            var _a;
            return ((_a = this.props.tree) === null || _a === void 0 ? void 0 : _a.onExpand) ? this.props.tree.onExpand : null;
        };
        this.getCollapseValue = () => {
            var _a;
            return ((_a = this.props.tree) === null || _a === void 0 ? void 0 : _a.collapseValue)
                ? this.props.tree.collapseValue
                : null;
        };
        this.optionsFromOptionList = null;
        this.getOptionsForFocusContext = () => {
            return this.props.options != null ||
                typeof this.props.children !== 'function'
                ? this.props.options
                : this.optionsFromOptionList != null
                    ? this.optionsFromOptionList
                    : null;
        };
        this.setOptionsFromOptionList = (optionData) => {
            this.optionsFromOptionList = optionData;
        };
        this.getOptionList = () => {
            if (this.checkDrillIn())
                return React.createElement(SelectDrillInContent, Object.assign({}, this.props.drillIn));
            if (this.props.loading)
                return this.getLoading();
            if (typeof this.props.children !== 'function' &&
                (!this.props.options || this.props.options.length === 0))
                return this.getEmptyResult();
            return (React.createElement(OptionList, { options: this.props.options, multiple: !!this.props.multiple, flatGroup: this.getFlatGroup(), value: this.props.multiple
                    ? this.state.checkboxState
                    : this.state.radioState, onChange: this.getOnChange, optionOnKeyDown: this.handleKeyDown, focusValue: this.context.focusState.optionFocus, className: "Select__OptionList", onExpand: this.getOnExpand(), collapseValue: this.getCollapseValue(), getOptionsData: this.setOptionsFromOptionList, indeterminateValue: this.props.indeterminateValue }, this.props.children));
        };
        /*
         * UI get functions
         * Options, Header, Footer, Trigger
         */
        this.getHeader = () => {
            if (this.checkDrillIn()) {
                return (React.createElement(SelectDrillInHeader, Object.assign({ closeSelectHandler: this.toggleOpenState, preventFocus: !!this.props.open }, this.props.drillIn)));
            }
            if (this.props.header || this.props.search) {
                return (React.createElement(AnvilSelectHeader, Object.assign({ search: !!this.props.search, closeSelectHandler: this.toggleOpenState }, this.props.search, this.props.header)));
            }
            return null;
        };
        this.getFooter = () => {
            if (this.checkDrillIn()) {
                if (this.props.drillIn.footerActionName &&
                    this.props.drillIn.footerOnActionClick) {
                    return (React.createElement(SelectDrillInFooter, Object.assign({ closeSelectHandler: this.toggleOpenState }, this.props.drillIn)));
                }
                return null;
            }
            if (React.isValidElement(this.props.footer)) {
                return React.cloneElement(this.props.footer);
            }
            if (this.props.footer ||
                (this.props.multiple && this.getMultipleValues().selectAll)) {
                return (React.createElement(AnvilSelectFooter, Object.assign({ selectAll: this.props.multiple &&
                        this.getMultipleValues().selectAll, onSelectAllClick: this.handleOnSelectAll, onSelectNoneClick: this.handleOnClear, closeSelectHandler: this.toggleOpenState }, this.props.footer)));
            }
            return null;
        };
        this.getTrigger = () => {
            if (React.isValidElement(this.props.trigger)) {
                return React.cloneElement(this.props.trigger, {
                    onClick: this.toggleOpenState,
                });
            }
            const triggerValue = this.props.multiple
                ? this.state.checkboxState
                : this.state.radioState;
            return (React.createElement(Trigger, Object.assign({ open: this.state.openState, disabled: this.props.disabled, error: this.props.error, value: triggerValue, onClearClick: this.handleOnClear, onClose: !this.props.tree && this.handleOnTagClose, onBlur: this.blur, onKeyDown: this.props.onKeyDown }, this.props.trigger, { onClick: this.handleOnClick })));
        };
        this.getLoading = () => {
            return (React.createElement(Stack, { justifyContent: "center" },
                React.createElement(Spinner, { size: "tiny" })));
        };
        /* Popover props */
        this.getRestOfProps = () => {
            const _a = this.props, { children, autoFlipHorizontally, autoFlipVertically, className, closeOnClickOutside, disabled, drillIn, emptyResult, error, footer, header, loading, multiple, onChange, onClickOutside, open, options, popoverWidth, search, trigger, value, autoFocus, onOpenChange, tree } = _a, rest = __rest(_a, ["children", "autoFlipHorizontally", "autoFlipVertically", "className", "closeOnClickOutside", "disabled", "drillIn", "emptyResult", "error", "footer", "header", "loading", "multiple", "onChange", "onClickOutside", "open", "options", "popoverWidth", "search", "trigger", "value", "autoFocus", "onOpenChange", "tree"]);
            return rest;
        };
        this.state = {
            checkboxState: props.value || [],
            radioState: props.value,
            openState: !!props.open,
            controlledMode: false,
        };
    }
    componentDidMount() {
        if (this.props.autoFocus) {
            const initialState = getInitialState(this.props.search, this.props.header, this.getOptionsForFocusContext(), false, !!this.props.tree);
            if (!this.state.openState) {
                initialState.triggerFocus = true;
            }
            this.context.focusDispatch({
                type: FocusActionType.SetFocus,
                payload: initialState,
            });
        }
        /* Show a console warning when using function children */
        if (typeof this.props.children === 'function') {
            console.warn('🚧 ANVIL: Using a function as children for AnvilSelect (and OptionList) is currently an early feature with limited functionality.');
        }
    }
    componentDidUpdate(prevProps) {
        const newValue = this.props.value;
        // If component updated but value didn't change, don't change state at all
        if (prevProps.value === newValue)
            return;
        // If component updated and value changed, update state to be new value
        this.setState(Object.assign(Object.assign({}, this.state), AnvilSelectWrapped.getNewCheckboxAndRadioStateByValue(newValue, this.props.options)));
    }
    static getDerivedStateFromProps(props, state) {
        const newValue = props.value;
        if (!state.controlledMode && newValue !== undefined && props.onChange) {
            return Object.assign({ controlledMode: true }, AnvilSelectWrapped.getNewCheckboxAndRadioStateByValue(newValue, props.options));
        }
        if (state.controlledMode &&
            (newValue !== state.checkboxState || newValue !== state.radioState)) {
            return AnvilSelectWrapped.getNewCheckboxAndRadioStateByValue(newValue, props.options);
        }
        return null;
    }
    render() {
        var _a;
        if (!this.state.openState)
            return this.getTrigger();
        const AnvilSelectClasses = classnames('a-Select', this.props.className, {
            'Select--DrillIn': (_a = this.props.drillIn) === null || _a === void 0 ? void 0 : _a.open,
            'Select--disabled': this.props.disabled,
            'Select--tree': this.props.tree,
            'Select--flat': !this.props.tree,
        });
        return (React.createElement(Popover, Object.assign({ autoFlipVertically: this.props.autoFlipVertically, autoFlipHorizontally: this.props.autoFlipHorizontally, className: AnvilSelectClasses, direction: "br", footer: this.state.openState && this.getFooter(), header: this.state.openState && this.getHeader(), open: this.state.openState, sharp: true, trigger: this.getTrigger(), width: this.props.popoverWidth, onClickOutside: this.handleClickOutside, "data-anvil-component": "AnvilSelect", portalDataAttributes: this.props.portalDataAttributes }, this.getRestOfProps()), this.state.openState && this.getOptionList()));
    }
}
AnvilSelectWrapped.defaultProps = {
    autoFlipVertically: true,
    closeOnClickOutside: true,
    popoverWidth: '100',
    portal: true,
};
AnvilSelectWrapped.contextType = FocusContext;
AnvilSelectWrapped.getCheckboxOptionByValue = (value, options) => {
    if (!Array.isArray(value) ||
        !value.length ||
        value === undefined ||
        value === null)
        return [];
    const isPrimitiveValueType = typeof value[0] !== 'object';
    if (isPrimitiveValueType) {
        return options.filter((option) => value.includes(option.value));
    }
    return value;
};
AnvilSelectWrapped.getRadioOptionByValue = (value, options) => {
    if (Array.isArray(value) || value === undefined || value === null)
        return undefined;
    const isPrimitiveValueType = typeof value !== 'object';
    if (isPrimitiveValueType) {
        return options.find((option) => option.value === value);
    }
    return value;
};
AnvilSelectWrapped.getNewCheckboxAndRadioStateByValue = (newValue, options) => {
    return {
        checkboxState: AnvilSelectWrapped.getCheckboxOptionByValue(newValue, options),
        radioState: AnvilSelectWrapped.getRadioOptionByValue(newValue, options),
    };
};
/* Wrap Select around focus management */
export const AnvilSelect = React.forwardRef((props, ref) => {
    return (React.createElement(FocusProvider, { header: props.header, search: props.search, options: props.options, preventFocus: true },
        React.createElement(AnvilSelectWrapped, Object.assign({}, props, { ref: ref }))));
});
AnvilSelect.displayName = 'AnvilSelect';
//# sourceMappingURL=AnvilSelect.js.map